import { CONTACT, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * Privacy Policy for the Shoutt platform, as approved by counsel. Cross-refers
 * to the General Terms (terms.constants.ts) and the Rentals Terms
 * (rentalTerms.constants.ts) by section number — keep the three in step.
 */
export const PRIVACY_CONTENT: LegalDocumentContent = {
  title: 'Privacy Policy',
  summary: `What personal data ${SITE.name} collects, why it is collected, how it is used, when it may be shared, how long it is retained, and the rights available to you.`,
  sections: [
    {
      id: 'introduction',
      heading: '1. Introduction',
      paragraphs: [
        `${LEGAL_ENTITY.name} ("Shoutt", "Company", "we", "us", or "our") operates the ${SITE.name} website, mobile application, and related services (the "Platform"), which is built around a college/student community and enables users to verify their student affiliation, communicate with each other, post requests, list items, and rent items from one another.`,
        'This Privacy Policy explains what personal data Shoutt collects, why it is collected, how it is used, when it may be shared, how long it is retained, and the rights available to you ("User", "you", "your"). This Policy should be read together with Shoutt\'s General Terms & Conditions and Rentals Terms & Conditions (together, the "Terms"), which are incorporated by reference and take precedence on contractual matters not addressed here.',
      ],
    },
    {
      id: 'scope',
      heading: '2. Scope',
      paragraphs: [
        'This Policy applies to personal data that Shoutt processes in connection with the Platform, including account registration and college/student verification, community and communication features, listing and renting items ("Rentals"), and associated payments. It applies to Users of the Platform in India.',
        'This Policy does not apply to third-party websites, applications, or services that you may access through the Platform, which are governed by their own privacy policies (see Section 13).',
      ],
    },
    {
      id: 'information-we-collect',
      heading: '3. Information We Collect',
      paragraphs: ['Shoutt collects the following categories of personal data:'],
      bullets: [
        '(a) Account and registration information: your name, email address (including your college/institutional email address), and password/login credentials, and any other information you provide when creating an Account.',
        '(b) College/student verification information: your college or institutional email address, used to verify your affiliation with an eligible college/student community (see Section 5).',
        '(c) Profile information: any profile details you choose to add, such as a profile photo, college/course name, or bio, to the extent the Platform offers such fields.',
        '(d) User Content: listings, photographs, item descriptions, reviews, ratings, and other content you post or upload to the Platform.',
        '(e) Communications: messages you exchange with other Users through the Platform’s in-app communication features, and your communications with Shoutt (for example, support or grievance requests).',
        '(f) Rental and transaction information: details of items listed or rented, the Rental Period, Deposit and Platform Fee amounts, cancellation and refund history, and information submitted in connection with a dispute, damage report, or refund/compensation claim, including any evidence you choose to provide (Rental Terms, Sections 5, 6 and 12).',
        '(g) Payment information: Shoutt does not use a third-party payment gateway or payment partner. Payments and Deposits in connection with Rentals are handled and processed manually by Shoutt itself, and Shoutt accordingly collects the payment and Deposit amounts, transaction status, and related details necessary to process a transaction manually.',
        '(h) Device and technical information: basic technical information generated through your use of the Platform, such as IP address, device/browser type, and app usage logs, to the extent necessary to operate and secure the Platform.',
        'Shoutt does not knowingly collect biometric data or precise location data, and does not use your personal data for advertising or ad-targeting purposes.',
      ],
    },
    {
      id: 'how-we-use',
      heading: '4. How We Use Personal Information',
      paragraphs: ['Shoutt uses personal data to:'],
      bullets: [
        'create, verify, and administer your Account, including college/student verification;',
        'operate, provide, maintain, and improve the Platform and its features;',
        'enable communication and interaction between Users;',
        'facilitate listings, Rental bookings, Deposits, payments, cancellations, and refunds;',
        'provide customer support and process grievances (General Terms, Section 14; Rental Terms, Section 16);',
        'review and resolve disputes, damage reports, and refund or compensation claims (Rental Terms, Sections 12 and 14);',
        'prevent, detect, and address fraud, abuse, security incidents, and violations of Shoutt’s Terms;',
        'comply with applicable law, regulatory requirements, and lawful requests from competent authorities; and',
        'send you Account, transaction, security, and service-related communications (General Terms, Section 15).',
        'Shoutt does not use personal data for purposes unrelated to operating the Platform, such as selling data to third parties.',
      ],
    },
    {
      id: 'student-verification',
      heading: '5. College/Student Verification',
      paragraphs: [
        'Shoutt is designed around a college/student community. Your college or institutional email address is collected and used solely to verify your affiliation with an eligible college/student community and to enable access to the Platform. It is not used for any unrelated marketing or commercial purpose.',
      ],
    },
    {
      id: 'user-content',
      heading: '6. User-Generated Content and Communications',
      paragraphs: [
        'Content you post, such as listings, photographs, descriptions, and reviews, may be visible to other Users or, where a feature is public-facing, other visitors to the Platform, in accordance with the licence you grant to Shoutt under the General Terms (Section 4). Messages you exchange with other Users through the Platform are processed to enable communication and, where relevant, to review disputes or enforce Shoutt’s Terms. You should avoid including sensitive personal information in listings or messages beyond what is necessary for the transaction.',
      ],
    },
    {
      id: 'rental-payment-data',
      heading: '7. Rental, Payment and Transaction Information',
      paragraphs: [
        'In connection with Rentals, Shoutt processes information about listings, bookings, Deposits, cancellations, and payments in order to facilitate the transaction between an Owner and a Renter, calculate and process the Platform Fee and applicable taxes such as GST, process refunds, and review disputes or damage/refund claims that a User raises with Shoutt (Rental Terms, Sections 5, 6, and 12). Consistent with the Rental Terms, Shoutt is not a party to the underlying rental agreement between an Owner and a Renter, and this information is used only to the extent necessary to provide the facilitation, payment, and dispute-review functions described in the Rental Terms.',
      ],
    },
    {
      id: 'sharing',
      heading: '8. Sharing and Disclosure of Information',
      paragraphs: ['Shoutt may share personal data:'],
      bullets: [
        'with other Users, to the extent necessary to enable a listing, communication, or Rental transaction (for example, an Owner and a Renter may see each other’s name and the transaction details necessary to complete a Rental);',
        'with service providers who support Platform operations, such as hosting/cloud infrastructure or customer support tools, under confidentiality obligations and only for the purposes for which they are engaged;',
        'with law enforcement, courts, or regulatory/government authorities, where required by law, in connection with a legal process, or to protect the rights, property, or safety of Shoutt, its Users, or the public, including in connection with the record-preservation and cooperation described in the General Terms (Section 12) and Rental Terms (Section 13.2);',
        'with professional advisors, such as legal counsel or auditors, where necessary; and',
        'as otherwise required to comply with applicable law.',
        'As Shoutt handles and processes payments and Deposits manually itself, payment-related information is not shared with any third-party payment partner or payment gateway. Shoutt does not sell personal data to third parties.',
      ],
    },
    {
      id: 'retention',
      heading: '9. Data Retention',
      paragraphs: [
        'Shoutt retains personal data for as long as necessary to provide the Platform, fulfil the purposes described in this Policy, comply with legal, tax, or regulatory obligations, resolve disputes, and enforce Shoutt’s Terms. Rental, payment, and dispute-related records may be retained for longer periods where necessary for these purposes.',
      ],
    },
    {
      id: 'security',
      heading: '10. Data Security',
      paragraphs: [
        'Shoutt takes reasonable technical and organisational measures designed to protect personal data against unauthorised access, loss, misuse, or alteration, consistent with the reasonable security practices required under applicable Indian data-protection law. You are responsible for maintaining the confidentiality of your Account credentials (General Terms, Section 2). No method of transmission or storage is completely secure, and Shoutt cannot guarantee absolute security.',
      ],
    },
    {
      id: 'your-rights',
      heading: '11. User Rights and Choices',
      paragraphs: [
        'Subject to applicable law, including the Digital Personal Data Protection Act, 2023, you may have the right to:',
      ],
      bullets: [
        'access information about the personal data Shoutt holds about you and how it is processed;',
        'seek correction, completion, or updating of your personal data;',
        'request erasure of your personal data, subject to Shoutt’s legal, contractual, and record-keeping obligations;',
        'withdraw consent for processing that is based on consent, without affecting the lawfulness of processing carried out before withdrawal;',
        'nominate another individual to exercise your rights in the event of death or incapacity, where applicable; and',
        'raise a grievance with Shoutt’s Grievance/Privacy Officer (Section 16) and, thereafter, with the Data Protection Board of India, where applicable.',
        'You may exercise these rights by contacting Shoutt using the details in Section 16.',
      ],
    },
    {
      id: 'cookies',
      heading: '12. Cookies and Similar Technologies',
      paragraphs: [
        'Based on the information available at the time of this draft, Shoutt does not use cookies or similar technologies for advertising or cross-site tracking. Basic technical mechanisms, such as session tokens, may be used where necessary for the Platform to function securely.',
      ],
    },
    {
      id: 'third-party-services',
      heading: '13. Third-Party Services and Links',
      paragraphs: [
        'The Platform may contain links to, or integrations with, other third-party websites or applications (General Terms, Section 16). This does not include payment processing, which Shoutt handles itself as described in Sections 3, 7, and 8. Shoutt does not control these third parties, and their collection and use of your information is governed by their own privacy policies, not this Policy. We encourage you to review the privacy policy of any third-party service before using it.',
      ],
    },
    {
      id: 'minors',
      heading: '14. Minors',
      paragraphs: [
        'The Platform is intended for use by individuals who are at least 18 years of age and legally competent to enter into a binding contract (General Terms, Section 2; Rental Terms, Section 7.1). Shoutt does not knowingly collect personal data from individuals under 18 years of age. If Shoutt becomes aware that it has collected personal data from a person under 18, it will take steps to delete such data and/or terminate the relevant Account, subject to applicable law.',
      ],
    },
    {
      id: 'changes',
      heading: '15. Changes to this Privacy Policy',
      paragraphs: [
        'Shoutt may update this Privacy Policy from time to time. Material changes will be notified through the Platform or by other reasonable means before they take effect, where required by applicable law. Your continued use of the Platform after an update takes effect constitutes acceptance of the revised Policy, to the extent permitted by applicable law.',
      ],
    },
    {
      id: 'grievance',
      heading: '16. Grievance Officer / Privacy Contact',
      paragraphs: [
        'For any questions, concerns, or grievances relating to this Privacy Policy or Shoutt’s processing of your personal data, please contact:',
      ],
      bullets: [
        `Grievance Officer: ${LEGAL_ENTITY.grievanceOfficer}`,
        `Designation: ${LEGAL_ENTITY.grievanceOfficerDesignation}`,
        `Email: ${CONTACT.grievanceEmail}`,
      ],
    },
    {
      id: 'contact',
      heading: '17. Contact Information',
      bullets: [
        `Company: ${LEGAL_ENTITY.name}`,
        `Platform/Brand: ${SITE.name}`,
        `Registered Office: ${LEGAL_ENTITY.address}`,
        `Email: ${CONTACT.email}`,
      ],
    },
  ],
};
